import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import StarRating from "../components/StarRating";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { toast } from "react-toastify";

const AddReviewModal = ({ show, onClose, mockupId, onReviewAdded, existingReview = null }) => {
  const [rating, setRating] = useState(0);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  
  const isEditMode = !!existingReview;

  // Pre-fill form when existing review is provided
  useEffect(() => {
    if (existingReview) {
      setRating(existingReview.rating || 0);
      setDescription(existingReview.description || existingReview.comment || "");
    } else {
      setRating(0);
      setDescription("");
    }
  }, [existingReview, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    if (!description.trim()) {
      toast.error("Please enter a review description");
      return;
    }

    setLoading(true);
    try {
      let result;
      
      if (isEditMode && existingReview?.id) {
        // Update existing review
        result = await apiClient.patch(`/reviews/${existingReview.id}/`, {
          rating: rating?.toFixed(2),
          description: description.trim(),
        });
      } else {
        // Create new review
        result = await apiClient.post("/reviews/", {
          listing: mockupId,
          rating: rating?.toFixed(2),
          description: description.trim(),
        });
      }

      if (!result.ok) {
        handleErrors(result.data);
        setLoading(false);
        return;
      }

      toast.success(isEditMode ? "Review updated successfully!" : "Review added successfully!");
      setRating(0);
      setDescription("");
      onClose();
      if (onReviewAdded) {
        onReviewAdded();
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} review:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'add'} review. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setDescription("");
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? "Edit Review" : "Add a Review"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Rating</Form.Label>
            <div>
              <StarRating
                rating={rating}
                readOnly={false}
                size="large"
                onChange={(newValue) => setRating(newValue)}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Your Review</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Share your experience with this mockup..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              style={{
                backgroundColor: "#C93312",
                border: "none",
              }}
              disabled={loading || rating === 0}
            >
              {loading ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update Review" : "Submit Review")}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddReviewModal;

